import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { localeDateFormat } from '../../../utils/localeUtils'

interface UseDateTimePickerProps {
  value: Date | null
  onChange: (selectedDateTime: Date | null, keyboardInputValue?: string) => void
  customOnClose?: (fullDate: Date) => void
}

const isValidDate = (date: any): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime())

const formatDisplayValue = (date: Date | null): string => isValidDate(date) ? format(date, `${localeDateFormat} HH:mm`) : ''

export function useDateTimePicker({ value, onChange, customOnClose }: UseDateTimePickerProps) {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showTimePicker, setShowTimePicker] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(value)
  const [displayValue, setDisplayValue] = useState(formatDisplayValue(value))

  const prevShowTimePicker = useRef(showTimePicker)

  useEffect(() => {
    setSelectedDate(value ?? new Date())
    setDisplayValue(formatDisplayValue(value))
  }, [value])

  useEffect(() => {
    if (prevShowTimePicker.current === true && showTimePicker === false) {
      if (customOnClose && selectedDate) {
        customOnClose(selectedDate)
      }
    }
    prevShowTimePicker.current = showTimePicker
  }, [showTimePicker, selectedDate, customOnClose])

  const handleDateSelected = (date: Date | null) => {
    if (!date) return

    let newDateTime: Date
    
    if (isValidDate(value)) {
      newDateTime = new Date(date)
      newDateTime.setHours(value.getHours(), value.getMinutes(), 0, 0)
    } else {
      const now = new Date()
      newDateTime = new Date(date)
      newDateTime.setHours(now.getHours(), now.getMinutes(), 0, 0)
    }

    setSelectedDate(newDateTime)
  }

  const handleTimeSelected = (time: Date | null) => {
    if (!time || !selectedDate) return

    const newDateTime = new Date(selectedDate)
    newDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0)
    
    setSelectedDate(newDateTime)
    setDisplayValue(formatDisplayValue(newDateTime))
    
    onChange(newDateTime)
  }

  const openDatePicker = () => {
    setShowDatePicker(true)
  }

  const closeDatePicker = () => {
    setShowDatePicker(false)
  }

  const closeTimePicker = () => {
    setShowTimePicker(false)
  }

  const handleDayButtonClick = () => {
    closeDatePicker()
    // Small delay to ensure smooth transition
    setTimeout(() => setShowTimePicker(true), 100)
  }

  return {
    showDatePicker,
    showTimePicker,
    selectedDate,
    displayValue,
    handleDateSelected,
    handleTimeSelected,
    openDatePicker,
    closeDatePicker,
    closeTimePicker,
    setShowTimePicker,
    handleDayButtonClick,
  }
}
